<template>
  <div
    v-show="showTableContent"
    class="directory-order-table-components"
  >
    <div
      v-if="!!items"
      class="table-header-bar"
    >
      <div class="header-left-block float-left">
        <label class="title">{{ titleMap[viewType] }}</label>
        <span class="last-update__text">{{ lastUpdated }}</span>
      </div>
      <div class="header-right-block float-right">
        <DropdownMenu
          v-show="!isFloor"
          :options="screenList"
          :format="(item, length) => `Screen ${item} of ${length}`"
          :selected="currentScreen"
          :afterSelect="val => currentScreen = val"
          :withAll="true"
        />
        <DropdownMenu
          v-show="!isFloor"
          :options="displayList"
          :format="(item, length) => `Column ${item} of ${length}`"
          :selected="currentColumn"
          :afterSelect="val => currentColumn = val"
          :withAll="true"
        />
      </div>
    </div>
    <div class="directory-table-content">
      <table class="directory-order-table">
        <thead>
          <tr>
            <th class="tenancyId">
              {{ $t('directory.label.tenancyRecordId') }}
            </th>
            <th
              v-if="isFloor"
              class="premises"
            >
              {{ $t('directory.label.premises') }}
            </th>
            <th
              v-for="header in headers"
              :key="header.attr"
              :class="header.cls"
            >
              {{ header.title }}
            </th>
            <th class="effective-date">
              {{ $t('directory.label.effectiveDate') }}
            </th>
            <th
              v-if="!isFloor"
              class="screen"
            >
              {{ $t('directory.label.screen') }}
            </th>
            <th class="display">
              {{ isFloor ? $t('directory.label.display') : $t('directory.label.column') }}
            </th>
            <th class="onHold">
              {{ $t('directory.label.onHold') }}
            </th>
            <th class="status">
              {{ $t('directory.label.status') }}
            </th>
            <th class="order">
              {{ $t('directory.label.order') }}
            </th>
            <th class="action" />
            <th class="action" />
          </tr>
        </thead>
        <DirectoryOrderTableRow
          v-for="(row, index) in displayTableRows"
          :key="row.item.id"
          :viewType="viewType"

          :directory="row.item"
          :displayItems="row.displayItems"
          :orderOptions="row.orderOptions"
          :index="index"
          :actionSettings="row.actionSettings"

          :disableAllExceptLinks="false"
          :windowScrollY="windowScrollY"
        />
      </table>
    </div>
    <ActionConfirmModal
      ref="removePublishedConfirmModal"
      :confirmTitle="$t('directory.msg.removePublishedConfirm.title')"
    >
      <div slot="content">
        <div class="confirm-remove-table">
          <CommonTable
            ref="removePublishedConfirmModal"
            :needCache="false"
            :tableContentLists="currentRemoveDirectory"
            :renderHeaderList="confirmRemoveTableHeader"
            :resizeColumnWhenContentChange="false"
            :defaultOrder="false"
            :needFilter="false"
            tableId="confirm-remove-table"
            :minWidth1800px="false"
          />
        </div>
        <div class="modal-pure-text">
          {{ $t('directory.msg.removePublishedConfirm.message') }}
        </div>
      </div>
    </ActionConfirmModal>
  </div>
</template>
<script>
import lodash from 'lodash'
import { mapActions } from 'vuex'

import ActionConfirmModal from '@/controls/ActionConfirmModal'
import CommonTable from '@/controls/CommonTable'
import DottedDropdown from '@/controls/DottedDropdown'

import DirectoryOrderTableRow from './DirectoryOrderTableRow'
import DropdownMenu from './DropdownMenu'

import { utils as formUtils } from '@/plugin/InputChange'
import dateUtil from '@/utils/dateUtil'
import navigation from '@/utils/navigation'
import { aclHelper, ccdPermsTypes, tmsPermsTypes } from '@/utils/aclHelper'
import { DIR_TYPE, DIR_STATUS,
  DIR_ACTION, DIR_DROPDOWN_ALL, defaultEmptyLobbyGroups, DUPLICATE_DIRECTORY_OFFSET,
  DIR_FLOOR_DISPLAYS, DIR_LOBBY_DISPLAYS, defaultEmptyFloorGroups, DIR_LOBBY_SCREENS, MAINTAIN_HISTORY_TYPE } from './consts'
import { ActionTypes, ccdRouteNames, directoryRouteNames } from '@/utils/consts'
import { headersMap, titleMap } from './directoryOrderTableHelper'
import {
  isDuplicatedDirectory,
  isPublishedInMaintain,
  dateFilterFunc,
  searchCompanyParamsFilterFunc,
  preProcessDirectories,
} from './utils'

export default {
  name: 'DirectoryOrderTable',
  components: {
    CommonTable,
    DottedDropdown,
    DropdownMenu,
    ActionConfirmModal,
    DirectoryOrderTableRow,
  },
  data () {
    return {
      collapseItems: [],
      headersMap,
      DIR_TYPE,
      titleMap,
      displayList: [],
      screenList: DIR_LOBBY_SCREENS,
      items: [],
      viewType: undefined,
      currentColumn: DIR_DROPDOWN_ALL,
      currentScreen: DIR_DROPDOWN_ALL,
      itemGroups: null,
      itemGroupsSnapshot: null,
      searchParams: {},
      searchCompanyParams: {},
      filterParams: {
        date: null,
      },
      originDirectories: [],
      maintainHistories: [],
      windowScrollY: 0,
      currentRemoveDirectory: [],
      confirmRemoveTableHeader: [],
    }
  },
  methods: {
    ...mapActions('directories', ['loadAll', 'batchUpdate', 'submitDirectory',
      'logMaintainHistory', 'listMaintainHistories']),
    ...mapActions('directories', { hardDeleteDirectory: 'deleteDirectory' }),
    formatDate (dateString, includeTime = false) {
      return dateString
        ? dateUtil.formatDate(dateString, includeTime)
        : this.$t('emptyValue')
    },
    isDuplicate (directory) {
      return isDuplicatedDirectory(directory, this.itemMap[directory.sourceId]) && this.itemMap[directory.sourceId]
    },
    isUncertain (screen, column, isFloor) {
      if (isFloor === undefined) {
        isFloor = this.isFloor
      }
      return isFloor ? !column : (!screen || !column)
    },
    isTheLastDirectoryInGroup (directory) {
      const group = this.isFloor ? this.itemGroups.floor : lodash.get(this.itemGroups, [directory.screen, directory.position], [])
      const lastItem = lodash.findLast(
        group.sort((item1, item2) => item1.order - item2.order),
      )
      return directory.id === lastItem.id
    },
    getOrderOptions (screen, column) {
      const group = this.isFloor ? this.itemGroups.floor : lodash.get(this.itemGroups, [screen, column], [])
      const options = group.map(item => item.order)
      return lodash.uniq(options).sort((order1, order2) => order1 - order2)
    },
    moveUp (item) {
      this.changeOrder(item, item.order - 1)
    },
    moveDown (item) {
      this.changeOrder(item, (+item.order) + 1)
    },
    changeOrder (item, order) {
      /**
       * When the function is called, the order of the directory item will move to the target order.
       * It means that if currentOrder < targetOrder, then all the orders of the directories between these two order
       * will have to move up (order--) to fill the gap. if currentOrder > targetOrder, all the orders of the directories
       * between these two order will have to move down (order++).
       */
      if (this.isDuplicate(item) || item.order === order) {
        return
      }
      item.order = item.order || Number.MAX_VALUE
      const moveStep = item.order > order ? 1 : -1
      const group = this.getGroup(item.screen, item.position)
      group.forEach(item2 => {
        if (
          (order <= item2.order && item2.order < item.order) ||
          (item.order < item2.order && item2.order <= order)) {
          item2.order += moveStep
        }
      })
      item.order = order
      if (item.child) {
        this.updateChild(item)
      }
      this.refreshGroup(item.screen, item.position)
      this.updateDirty()
    },
    changeColumn (item, column) {
      this.changeGroup(item, item.screen, column)
      this.updateDirty()
    },
    changeScreen (item, screen) {
      this.changeGroup(item, screen, item.position)
      this.updateDirty()
    },
    changeGroup (item, screen, column) {
      // If the item is duplicate directory, then deal with its father.
      if (this.isDuplicate(item)) {
        item = this.itemMap(item.sourceId)
      }
      // If item is in the target group, do nothing and return.
      if (item.screen === screen &&
        item.position === column) {
        return
      }
      const isFromGroupUncertain = this.isUncertain(item.screen, item.position)
      const isToGroupUncertain = this.isUncertain(screen, column)
      // All directory order in uncertain group is regarded as null
      // The directory with no order will be pushed to the end of the group.
      let order = item.order || Number.MAX_VALUE
      const fromGroup = this.getGroup(item.screen, item.position)
      const toGroup = this.getGroup(screen, column)

      if (!isFromGroupUncertain) {
        fromGroup.forEach(item2 => {
          if (item2.order > order) {
            item2.order -= 1
          }
        })
      }

      if (!isToGroupUncertain) {
        let maxOrder = Number.MIN_VALUE
        if (isFromGroupUncertain) {
          order = 1
        }
        toGroup.forEach(item2 => {
          if (item2.order >= order) {
            item2.order += 1
          }
          maxOrder = maxOrder < item2.order ? item2.order : maxOrder
        })
        item.order = order > maxOrder ? maxOrder + 1 : order
      }

      const previousScreen = item.screen
      const previousColumn = item.position
      item.screen = screen
      item.position = column
      if (item.child) {
        this.updateChild(item)
      }
      this.refreshGroup(screen, column)
      if (fromGroup !== toGroup) {
        this.refreshGroup(previousScreen, previousColumn)
      }
    },
    updateOnHold (item, val) {
      item.onHold = val
      this.updateChild(item)
      this.updateDirty()
    },
    getChild (directory) {
      return directory.child && !isPublishedInMaintain(directory.status, directory.action)
        ? this.itemMap[directory.child.id] : undefined
    },
    navToDirectoryList (item) {
      this.storeRouteQuery(() => {
        navigation.toPage(directoryRouteNames.tenancyDirectories, { id: item.tenancy.id })
      })
    },
    navToTenancy (item) {
      this.storeRouteQuery(() => {
        navigation.toPage(ccdRouteNames.ccdTenancyView, {id: item.tenancy.id})
      })
    },
    storeRouteQuery (callback) {
      const query = {}
      Object.keys(this.searchParams).forEach(key => {
        if (this.searchParams[key]) {
          query[key] = (this.searchParams[key])
        }
      })
      Object.keys(this.searchCompanyParams).forEach(key => {
        if (this.searchCompanyParams[key]) {
          const queryKey = key === 'floor' ? 'lobbyFloor' : key
          query[queryKey] = (this.searchCompanyParams[key])
        }
      })
      Object.keys(this.filterParams).forEach(key => {
        if (this.filterParams[key]) {
          query[key] = (this.filterParams[key])
        }
      })
      query.currentColumn = this.currentColumn
      query.currentScreen = this.currentScreen
      // trigger callback no matter Complete or Abort
      this.$router.replace({query}, callback, callback)
    },
    updateCurrentColumn (val) {
      this.currentColumn = val
    },
    updateCurrentScreen (val) {
      this.currentScreen = val
    },
    updateDirty () {
      formUtils.setDirty()
      this.updateVirtualScroll(true)
    },
    updateChild (directory) {
      if (!directory.child || !this.items || !this.items.length) {
        return
      }
      const child = this.itemMap[directory.child.id]
      if (child) {
        child.screen = directory.child.screen = directory.screen
        child.position = directory.child.position = directory.position
        child.order = directory.child.order = directory.order
        child.onHold = directory.child.onHold = directory.onHold
      }
    },
    getGroup (screen, column) {
      if (this.isUncertain(screen, column)) {
        return this.itemGroups.others
      } else if (this.isFloor) {
        return this.itemGroups.floor
      } else {
        return this.itemGroups[screen][column]
      }
    },
    setGroup (screen, column, group) {
      if (this.isUncertain(screen, column)) {
        this.itemGroups.others = group
      } else if (this.isFloor) {
        this.itemGroups.floor = group
      } else {
        this.itemGroups[screen][column] = group
      }
    },
    /**
     * Reacquire the group data.
     */
    refreshGroup (screen, column) {
      if (this.isUncertain(screen, column)) {
        this.itemGroups.others = this.getUncertains(this.items)
        return
      }
      const filterFunc = this.isFloor
        ? (item) => !this.isUncertain(item.screen, item.position)
        : (item) => item.screen === screen && item.position === column
      this.setGroup(screen, column, this.items.filter(filterFunc))
    },
    getUncertains (directories) {
      /**  In uncertain group, the dupilcate directory may not follow its father closely
       * because the directory in the group has no order.
       */
      const filteredItems = directories.filter(item =>
        this.isUncertain(item.screen, item.position))
      return this.sortUncertains(filteredItems)
    },
    sortUncertains (directories) {
      const filteredDirectories = directories.filter(directory => !this.isDuplicate(directory))
      const sortedFilteredDirectories = lodash.orderBy(filteredDirectories, directory => {
        const itemToBeChecked = directory.child ? directory.child : directory
        return (itemToBeChecked.status === DIR_STATUS.APPR && itemToBeChecked.action === DIR_ACTION.ADD)
          ? Number.MIN_VALUE : 1
      })
      const results = []
      sortedFilteredDirectories.forEach(directory => {
        results.push(directory)
        if (directory.child) {
          this.updateChild(directory)
          results.push(this.itemMap[directory.child.id])
        }
      })
      return results
    },
    generateItemGroups (directories, isFloor) {
      const groups = lodash.cloneDeep(!isFloor
        ? defaultEmptyLobbyGroups
        : defaultEmptyFloorGroups)
      directories.forEach(item => {
        if (!this.isUncertain(item.screen, item.position, isFloor)) {
          if (isFloor) {
            groups['floor'].push(item)
          } else {
            groups[item.screen][item.position].push(item)
          }
        } else {
          groups['others'].push(item)
        }
      })
      groups.others = this.sortUncertains(groups.others)
      return groups
    },
    reassignOrders (groups, isFloor) {
      let assignOrder =
        group => {
          const sortedNonDuplicateItems = lodash.orderBy(
            group.filter(item => !this.isDuplicate(item)),
            item => item.order || Number.MAX_VALUE)
          sortedNonDuplicateItems.forEach((item, index) => {
            item.order = index + 1
            if (item.child) {
              this.updateChild(item)
            }
          })
        }
      if (!isFloor) {
        const screenList = this.screenList
        const displayList = this.displayList
        screenList.forEach(screen => {
          displayList.forEach(column => {
            assignOrder(groups[screen][column])
          })
        })
      } else {
        assignOrder(groups.floor)
      }
    },
    async search (searchParams = {}, filterParams = {}, searchCompanyParams = {}) {
      if (searchParams.type && searchParams.building) {
        this.viewType = searchParams.type
        this.items = null
        this.itemGroups = null
        this.itemGroupsSnapshot = null
        if (searchParams.type === DIR_TYPE.FLOOR) {
          this.$set(this, 'displayList', DIR_FLOOR_DISPLAYS)
          this.currentScreen = DIR_DROPDOWN_ALL
          this.currentColumn = DIR_DROPDOWN_ALL
        } else {
          this.$set(this, 'displayList', DIR_LOBBY_DISPLAYS)
        }
        this.searchParams = lodash.cloneDeep(searchParams)
        this.filterParams = lodash.cloneDeep(filterParams)
        this.searchCompanyParams = lodash.cloneDeep(searchCompanyParams)
        await this.loadDirectories(searchParams)
      }
    },
    async loadDirectories (searchParams) {
      const directories = await this.loadAll({
        ...searchParams,
        status: [
          DIR_STATUS.CART, DIR_STATUS.SUBMITTED,
          DIR_STATUS.APPR, DIR_STATUS.PROCESSED, DIR_STATUS.PUBLISHED, DIR_STATUS.REJECTED],
      })
      this.originDirectories = lodash.cloneDeep(directories)
      const filteredDirectories = preProcessDirectories(directories, this.itemMap)
      const isFloor = searchParams.type === DIR_TYPE.FLOOR
      this.items = filteredDirectories
      this.items.filter(item => !this.isDuplicate(item)).forEach(item => {
        if (item.child) {
          this.updateChild(item)
        }
      })
      const groups = this.generateItemGroups(filteredDirectories, isFloor)
      this.reassignOrders(groups, isFloor)
      this.itemGroups = groups
      // Update screen/column on UI after SAVE - https://jirapct.epam.com/jira/browse/HKLDCMS-23412
      this.itemGroupsSnapshot = this.generateItemGroups(filteredDirectories, isFloor)
      this.maintainHistories = await this.listMaintainHistories(searchParams)
      formUtils.resetDirty()
    },
    async beforeRemoveDirectory (directory) {
      if (isPublishedInMaintain(directory.status, directory.action)) {
        const company = directory.companies[0]
        this.confirmRemoveTableHeader = [
          {
            title: this.$t('directory.label.officeName'),
            name: 'engName',
            defaultValue: '--',
          },
          {
            title: this.isFloor ? this.$t('directory.label.premises') : this.$t('directory.label.floor'),
            name: this.isFloor ? 'premises' : 'floor',
            defaultValue: '--',
          },
        ]
        this.currentRemoveDirectory = [company]
        this.$refs.removePublishedConfirmModal.open(this.removeDirectory, [directory])
      } else {
        await this.removeDirectory(directory)
      }
    },
    async removeDirectory (directory) {
      if (!isPublishedInMaintain(directory.status, directory.action)) {
        return
      }
      await this.submitDirectory({
        id: directory.id,
        action: DIR_ACTION.REMOVE,
        status: DIR_STATUS.PUBLISHED,
        needUpdate: true,
        refresh: false,
        request: {
          removeDate: new Date(),
          specificAttrs: true,
        },
      })
      await this.logMaintainHistory({
        ...this.searchParams,
        action: MAINTAIN_HISTORY_TYPE.REMOVE_DIRECTORY,
      })
      await this.deleteDirectory(directory.id, false)
      // After preprocessing, the child field of a published directory is
      // hidden, so when the 'removeDirectory' is called, the child id
      // is obtained from 'realChild' field.
      if (directory.realChild) {
        await this.deleteDirectory(directory.realChild.id, true)
        this.$delete(directory, 'realChild')
      }
      const group = this.getGroup(directory.screen, directory.position)
      if (!this.isUncertain(directory.screen, directory.position)) {
        group.forEach(item => {
          if (item.order > directory.order) {
            item.order -= 1
          }
        })
      }
      this.refreshGroup(directory.screen, directory.position)
      this.maintainHistories = await this.listMaintainHistories(this.searchParams)
      this.updateVirtualScroll(true)
    },
    async deleteDirectory (id, hardDelete) {
      const findAndSplice = (array, func) => {
        const index = array.findIndex(func)
        if (index >= 0) {
          array.splice(index, 1)
        }
      }
      findAndSplice(this.items, item => item.id === id)
      findAndSplice(this.originDirectories, item => item.id === id)
      if (hardDelete) {
        await this.hardDeleteDirectory({
          id: id,
        })
      }
    },
    async save (handleProcess = false) {
      const directories = this.items.map(item => {
        return {
          id: item.id,
          screen: item.screen,
          position: item.position,
          onHold: item.onHold,
          order: item.order,
          status: (handleProcess && this.itemsCanProcess.includes(item)) ? DIR_STATUS.PROCESSED : item.status,
          action: item.action,
          updatedAt: item.updatedAt,
        }
      })
      const updateParams = {
        directories: directories,
      }
      await this.batchUpdate({request: updateParams, headerAction: handleProcess ? ActionTypes.directories.maintainOrder.processed : ActionTypes.directories.maintainOrder.saved})
      await this.logMaintainHistory({
        ...this.searchParams,
        action: handleProcess ? MAINTAIN_HISTORY_TYPE.PROCESS : MAINTAIN_HISTORY_TYPE.SAVE,
      })
      await this.loadDirectories(this.searchParams)
      formUtils.resetDirty()
    },
    async process () {
      await this.save(true)
    },
    getPreviewData () {
      if (!this.originDirectories) {
        return undefined
      }
      const copiedOriginDirectories = lodash.cloneDeep(this.originDirectories)
      const isFloor = this.searchParams.type === DIR_TYPE.FLOOR
      const copiedGroups = lodash.cloneDeep(this.generateItemGroups(copiedOriginDirectories, isFloor))
      const date = new Date(this.filterParams.date)
      const filterAndSort = group => {
        return lodash.orderBy(
          group.filter(item => !item.onHold && dateFilterFunc(item, date)),
          'order',
        )
      }
      if (this.isFloor && copiedGroups.floor) {
        copiedGroups.floor = filterAndSort(copiedGroups.floor)
      } else {
        this.screenList.forEach(screen => {
          this.displayList.forEach(column => {
            copiedGroups[screen][column] = filterAndSort(copiedGroups[screen][column])
          })
        })
      }
      copiedGroups.date = date
      return copiedGroups
    },
    getProcessItems () {
      const sortFunction = item => {
        return this.isDuplicate(item) ? DUPLICATE_DIRECTORY_OFFSET : 0
      }
      return lodash.orderBy(this.itemsCanProcess, ['screen', 'position', 'order', sortFunction], ['desc', 'desc', 'desc', 'desc']).map(item => {
        return {
          directory: item,
          engName: item.companies[0].engName,
          floor: this.isFloor ? item.floor : item.companies[0].floor,
        }
      })
    },
    getDotOptions (item) {
      const results = []
      if (this.canRemoveDirectory && isPublishedInMaintain(item.status, item.action)) {
        results.push({
          name: this.$t('action.remove'),
          method: () => this.beforeRemoveDirectory(item),
        })
      }
      return results
    },
    getActionSettings (item, group, index) {
      return {
        clickTenancyId: {
          enabled: this.canReadTenancy,
          action: () => this.navToTenancy(item),
        },
        clickCompanyName: {
          enabled: this.canReadDirectory,
          action: () => this.navToDirectoryList(item),
        },
        moveUp: {
          enabled: !this.isUncertain(item.screen, item.position, this.isFloor) && (+item.order) > 1,
          action: () => this.moveUp(item),
        },
        moveDown: {
          enabled: !this.isUncertain(item.screen, item.position, this.isFloor) && !this.isTheLastDirectoryInGroup(item),
          action: () => this.moveDown(item),
        },
        dotOptions: {
          enabled: true,
          actions: this.getDotOptions(item),
        },
        changeOrder: {
          enabled: !this.isUncertain(item.screen, item.position, this.isFloor),
          action: order => this.changeOrder(item, order),
        },
        changeScreen: {
          enabled: true,
          action: screen => this.changeScreen(item, screen),
        },
        changeColumn: {
          enabled: true,
          action: column => this.changeColumn(item, column),
        },
        updateOnHold: {
          enabled: true,
          action: val => this.updateOnHold(item, val),
        },
      }
    },
    updateVirtualScroll (forceUpdate = false) {
      let windowScrollY = Math.floor(window.document.documentElement.scrollTop || window.scrollY)
      windowScrollY = forceUpdate === true
        ? (windowScrollY + Math.random()) // force to re-calculate after updateDirty
        : (windowScrollY - windowScrollY % 100) // 100px buffer for performance
      if (this.windowScrollY !== windowScrollY) this.windowScrollY = windowScrollY
    },
    bindVirtualScroll () {
      // throttle at interval of 100ms for performance
      this.__updateVirtualScroll = lodash.throttle(this.updateVirtualScroll, 100, {
        leading: true,
        trailing: true,
      })
      window.addEventListener('scroll', this.__updateVirtualScroll)
      window.addEventListener('resize', this.__updateVirtualScroll)
      this.__updateVirtualScroll()
    },
    unbindVirtualScroll () {
      window.removeEventListener('scroll', this.__updateVirtualScroll)
      window.removeEventListener('resize', this.__updateVirtualScroll)
    },
    parseRouteQuery () {
      const routeQuery = this.$router.currentRoute.query
      if (routeQuery.currentColumn) {
        this.updateCurrentColumn(routeQuery.currentColumn)
      }
      if (routeQuery.currentScreen) {
        this.updateCurrentScreen(routeQuery.currentScreen)
      }
    },
    matchFilterFunc (item) {
      return item &&
        dateFilterFunc(item, this.filterParams.date, this.itemMap) &&
        searchCompanyParamsFilterFunc(item, this.searchCompanyParams)
    },
  },
  computed: {
    headers () {
      return this.headersMap[this.viewType]
    },
    itemMap () {
      return lodash.keyBy(this.originDirectories, 'id')
    },
    displayItems () {
      const itemGroups = this.itemGroupsSnapshot
      if (!itemGroups) return

      const results = []
      const sortFunction = item => {
        // Update order on UI after SAVE - https://jirapct.epam.com/jira/browse/HKLDCMS-23412
        if (item.__initialOrder === undefined) {
          item.__initialOrder = item.order + (this.isDuplicate(item) ? DUPLICATE_DIRECTORY_OFFSET : 0)
        }
        return item.__initialOrder
      }
      const filterFunc = directories => directories.filter(directory => (!this.isDuplicate(directory)))

      const filterThenSort = directories => lodash.orderBy(filterFunc(directories), sortFunction)

      const uncertainItems = lodash.orderBy(itemGroups.others, [
        directory => this.isFloor ? directory.companies[0].premises : directory.companies[0].floor,
        directory => directory.companies[0].engName,
      ])
      if (uncertainItems.length) {
        results.push(filterFunc(uncertainItems))
      }
      if (this.isFloor) {
        results.push(filterThenSort(itemGroups.floor))
        return results
      }
      if (!this.currentColumn || !this.currentScreen) return

      const screenFilter = this.currentScreen === DIR_DROPDOWN_ALL ? this.screenList : [this.currentScreen]
      const columnFilter = this.currentColumn === DIR_DROPDOWN_ALL ? this.displayList : [this.currentColumn]
      screenFilter.forEach(screen => {
        columnFilter.forEach(column => {
          if (!itemGroups[screen][column] || itemGroups[screen][column].length > 0) {
            results.push(filterThenSort(itemGroups[screen][column]))
          }
        })
      })
      return results
    },
    displayTableRows () {
      return (this.displayItems || []).reduce(
        (rows, group, groupIndex) => {
          return rows.concat((group || []).map((item, index) => ({
            item,
            displayItems: [item, this.getChild(item)].filter(this.matchFilterFunc),
            actionSettings: this.getActionSettings(item, group, index),
            orderOptions: this.getOrderOptions(item.screen, item.position),
          })).filter(row => row.displayItems.length))
        }, [])
    },
    displayScreens () {
      return this.currentScreen === DIR_DROPDOWN_ALL ? this.screenList : [this.currentScreen]
    },
    displayColumns () {
      return this.currentColumn === DIR_DROPDOWN_ALL ? this.displayList : [this.currentColumn]
    },
    showTableContent () {
      return !!this.viewType
    },
    lastUpdated () {
      const lastMaintainHistory = this.maintainHistories[0]
      if (lastMaintainHistory) {
        return this.$t('lastUpdatedBy',
          [lastMaintainHistory.updater.displayName, this.formatDate(lastMaintainHistory.updatedAt, true)])
      } else {
        return ''
      }
    },
    uncertainItems () {
      return this.itemGroups ? this.itemGroups.others : []
    },
    canEditDirectory () {
      return aclHelper.getAccess(tmsPermsTypes.directory.maintain)
    },
    canReadDirectory () {
      return aclHelper.getAccess(tmsPermsTypes.directory.read)
    },
    canReadTenancy () {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.read)
    },
    canRemoveDirectory () {
      return aclHelper.getAccess(tmsPermsTypes.directory.removeFromMaintain)
    },
    isFloor () {
      return this.viewType === DIR_TYPE.FLOOR
    },
    allowSave () {
      return !!(this.items && this.items.length) && this.canEditDirectory
    },
    allowPreview () {
      if (!(this.items && this.items.length && this.filterParams.date)) {
        return false
      }
      const filterPreviewItems = []
      const filterUncertainItems = []
      const date = new Date(this.filterParams.date)
      this.items.forEach(item => {
        if (!item.onHold && dateFilterFunc(item, date)) {
          item.order ? filterPreviewItems.push(item) : filterUncertainItems.push(item)
        }
      })
      return filterPreviewItems.length !== 0 && filterUncertainItems.length === 0 && this.canEditDirectory
    },
    itemsCanProcess () {
      return (this.items || []).filter(item => !this.isUncertain(item.screen, item.position) && item.status === DIR_STATUS.APPR && !item.onHold)
    },
    allowProcess () {
      return !!this.itemsCanProcess.length && this.canEditDirectory
    },
  },
  watch: {
    allowPreview (value) {
      this.$emit('updateAllowPreview', value)
    },
    allowSave (value) {
      this.$emit('updateAllowSave', value)
    },
    allowProcess (value) {
      this.$emit('updateAllowProcess', value)
    },
  },
  async mounted () {
    this.parseRouteQuery()
    this.bindVirtualScroll()
  },
  destroyed () {
    this.unbindVirtualScroll()
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/table";
@import "~styles/directories/maintain";
@import '~styles/popup';

.directory-order-table-components {
  height: fit-content;
  box-shadow: 0 0 4px 0 rgba(172, 172, 172, 0.5);
  background-color: #ffffff;
  margin: 15px;
  .table-header-bar {
    height: 58px;
    width: 100%;
    display: inline-block;
    border-bottom: solid 1px #ebebeb;
    .header-left-block {
      height: inherit;
      align-items: center;
      display: flex;
    }
    .header-right-block {
      height: inherit;
      align-items: center;
      display: flex;
    }
    .title {
      margin: 19px 21px;
      font-family: Verdana;
      font-size: 16px;
      font-weight: bold;
      font-style: normal;
      font-stretch: normal;
      line-height: 1.25;
      letter-spacing: normal;
      color: #10416c;
    }
  }
  .directory-table-content {
    margin: 15px;
    .directory-order-table thead th {
      position: sticky;
      top: 65px;
      box-shadow: 0 1px 1px -1px rgba(0, 0, 0, 0.4);
      z-index: 1;
    }
  }
}

.confirm-remove-table {
  margin-top: 20px;
  margin-bottom: 20px;
  & ::v-deep .table-list {
    width: 100%;
  }
  & ::v-deep .table-content {
    width: 100% !important;
  }
  & ::v-deep .common-table-header-container {
    min-width: 100%;
    max-width: 100%;
  }
  & ::v-deep .common-table {
    min-width: 100%;
    max-width: 100%;
    min-height: unset;
    margin: 0;
    .engName {
      width: 75%;
    }
    .floor, .premises{
      width: 25%;
    }
  }
}
</style>
